import {
  ROLE_LABELS,
  creatableRolesFor,
  roleNeedsFaculty,
  roleNeedsStudyProgram,
  roleNeedsUnit,
  type CreateUserInput,
  type RegisterInput,
  type Role,
} from "@academichub/shared";
import type { MasterFaculty, MasterStudyProgram, MasterUnit, RegisterOptions } from "../api/types";

/**
 * Logika formulir Kelola Pengguna, dipisah dari komponen agar dapat diuji
 * tanpa DOM. Kewajiban lingkup mengikuti aturan bersama di packages/shared,
 * sehingga pilihan yang tampil tidak pernah berbeda dari yang diterima server.
 */

export interface UserFormState {
  username: string;
  fullName: string;
  email: string;
  identifierNumber: string;
  role: Role;
  facultyId: string;
  studyProgramId: string;
  unitId: string;
  password: string;
}

export function emptyUserForm(role: Role): UserFormState {
  return {
    username: "",
    fullName: "",
    email: "",
    identifierNumber: "",
    role,
    facultyId: "",
    studyProgramId: "",
    unitId: "",
    password: "",
  };
}

/** Pilihan peran yang boleh dibuat oleh peran pembuat (urut dari yang terbawah). */
export function roleOptions(
  actorRole: Role | undefined | null,
): Array<{ value: Role; label: string }> {
  return creatableRolesFor(actorRole).map((role) => ({ value: role, label: ROLE_LABELS[role] }));
}

/** Lingkup apa yang wajib diisi untuk peran yang dipilih. */
export function scopeRequirements(role: Role): {
  needsFaculty: boolean;
  needsStudyProgram: boolean;
  needsUnit: boolean;
} {
  return {
    needsFaculty: roleNeedsFaculty(role),
    needsStudyProgram: roleNeedsStudyProgram(role),
    needsUnit: roleNeedsUnit(role),
  };
}

const PASSWORD_UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const PASSWORD_LOWER = "abcdefghijkmnopqrstuvwxyz";
const PASSWORD_DIGITS = "23456789";
const PASSWORD_SPECIALS = "!@#$%*?";

/**
 * Kata sandi awal acak yang DIJAMIN memenuhi kebijakan (>= 10 karakter serta
 * memuat huruf besar, huruf kecil, dan angka).
 */
export function generatePassword(): string {
  const random = new Uint32Array(12);
  crypto.getRandomValues(random);
  const pick = (alphabet: string, index: number): string =>
    alphabet.charAt((random[index] ?? 0) % alphabet.length);

  const required = `${pick(PASSWORD_UPPER, 0)}${pick(PASSWORD_LOWER, 1)}${pick(PASSWORD_DIGITS, 2)}`;
  let filler = "";
  for (let index = 3; index < 10; index += 1) {
    filler += pick(index % 2 === 0 ? PASSWORD_LOWER : PASSWORD_UPPER, index);
  }
  return `${required}${filler}${pick(PASSWORD_SPECIALS, 10)}`;
}

/** Ubah state formulir menjadi body API; kolom kosong tidak ikut dikirim. */
export function buildCreateUserPayload(form: UserFormState): CreateUserInput {
  const { needsFaculty, needsStudyProgram, needsUnit } = scopeRequirements(form.role);

  if (needsStudyProgram && !form.studyProgramId) {
    throw new Error("Program studi wajib dipilih untuk peran tersebut.");
  }
  if (needsUnit && !form.unitId) {
    throw new Error("Unit wajib dipilih untuk peran Operator Unit.");
  }
  if (needsFaculty && !form.facultyId) {
    throw new Error("Fakultas wajib dipilih untuk peran tersebut.");
  }

  const payload: CreateUserInput = {
    username: form.username.trim(),
    fullName: form.fullName.trim(),
    email: form.email.trim(),
    role: form.role,
    password: form.password,
  };

  if (form.identifierNumber.trim()) payload.identifierNumber = form.identifierNumber.trim();
  if (needsStudyProgram) payload.studyProgramId = form.studyProgramId;
  if (needsUnit) payload.unitId = form.unitId;
  if (needsFaculty) payload.facultyId = form.facultyId;

  return payload;
}

/** Kalimat konfirmasi setelah akun berhasil dibuat. */
export function createdUserMessage(user: { username: string; role: Role }): string {
  return `Akun ${user.username} (${ROLE_LABELS[user.role]}) berhasil dibuat dan sudah dapat login.`;
}

export interface ScopeLookups {
  faculties: MasterFaculty[];
  programs: MasterStudyProgram[];
  units: MasterUnit[];
}

/** Label lingkup akun untuk tabel: fakultas, program studi, dan unit. */
export function describeUserScope(
  target: {
    facultyId: string | null;
    studyProgramId: string | null;
    unitId: string | null;
  },
  lookups: ScopeLookups,
): string {
  const parts: string[] = [];
  const faculty = lookups.faculties.find((item) => item.id === target.facultyId);
  const program = lookups.programs.find((item) => item.id === target.studyProgramId);
  const unit = lookups.units.find((item) => item.id === target.unitId);

  if (faculty) parts.push(faculty.name);
  if (program) parts.push(`${program.name} (${program.code})`);
  if (unit) parts.push(unit.name);

  return parts.length > 0 ? parts.join(" · ") : "Tingkat institut";
}

export interface RegisterFormState {
  username: string;
  fullName: string;
  email: string;
  identifierNumber: string;
  role: Role;
  studyProgramId: string;
  unitId: string;
  password: string;
}

export function emptyRegisterForm(role: Role = "MAHASISWA"): RegisterFormState {
  return {
    username: "",
    fullName: "",
    email: "",
    identifierNumber: "",
    role,
    studyProgramId: "",
    unitId: "",
    password: "",
  };
}

/** Pilihan peran pendaftaran (hanya peran yang boleh mendaftar sendiri). */
export function registerRoleOptions(
  roles: readonly Role[] | undefined,
): Array<{ value: Role; label: string }> {
  return (roles ?? []).map((role) => ({ value: role, label: ROLE_LABELS[role] }));
}

/** Program studi berlabel fakultas dari opsi pendaftaran publik. */
export function registerProgramOptions(
  options: RegisterOptions | undefined,
): Array<{ id: string; label: string }> {
  return (options?.faculties ?? []).flatMap((faculty) =>
    faculty.studyPrograms.map((program) => ({
      id: program.id,
      label: `${program.name} (${program.code}) — ${faculty.name}`,
    })),
  );
}

/** Unit yang berada pada program studi terpilih. */
export function registerUnitOptions(
  options: RegisterOptions | undefined,
  studyProgramId: string,
): Array<{ id: string; name: string }> {
  return (options?.units ?? [])
    .filter((unit) => unit.studyProgramId === studyProgramId)
    .map((unit) => ({ id: unit.id, name: unit.name }));
}

/** Ubah state formulir pendaftaran menjadi body API. */
export function buildRegisterPayload(form: RegisterFormState): RegisterInput {
  const { needsStudyProgram, needsUnit } = scopeRequirements(form.role);

  if (needsStudyProgram && !form.studyProgramId) {
    throw new Error("Program studi wajib dipilih.");
  }
  if (needsUnit && !form.unitId) {
    throw new Error("Unit wajib dipilih untuk peran Operator Unit.");
  }

  const payload: RegisterInput = {
    username: form.username.trim(),
    fullName: form.fullName.trim(),
    email: form.email.trim(),
    role: form.role,
    password: form.password,
  };

  if (form.identifierNumber.trim()) payload.identifierNumber = form.identifierNumber.trim();
  if (needsStudyProgram) payload.studyProgramId = form.studyProgramId;
  if (needsUnit) payload.unitId = form.unitId;

  return payload;
}
